import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Stack,
  Button,
  Chip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  LocalFireDepartment as CaloriesIcon,
  Restaurant as RestaurantIcon,
  ExpandMore as ExpandMoreIcon,
  Share as ShareIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { getMealTypeColor } from "../../client-meal-plan/theme/ClientTheme";
import { usePDFGenerator } from "../../../hooks/usePDFGenerator";

/**
 * Parse description to extract first sentence and remaining content
 * @param {string} description - The full description
 * @returns {{firstSentence: string, remainingContent: string}} - Parsed first sentence and remaining content
 */
const parseDescriptionSentences = (description) => {
  if (!description) return { firstSentence: "", remainingContent: "" };

  // Split by first sentence (ends with . ! or ?)
  const sentences = description.split(/(?<=[.!?])\s+/);

  if (sentences.length <= 1) {
    return { firstSentence: description, remainingContent: "" };
  }

  const firstSentence = sentences[0].trim();
  const remainingContent = sentences.slice(1).join(" ").trim();

  return { firstSentence, remainingContent };
};

/**
 * Public recipe viewer content for clients
 * Shows full recipe information including ingredients, instructions, and nutrition
 * Matches the styling from ClientRecipeDetails in meal plans
 */
const ClientRecipeContent = ({ recipe, organizationBranding }) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState(
    new Set(["ingredients", "shopping", "instructions"]),
  );
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // PDF generation hook
  const { generatePDFFromHTML, isGenerating } = usePDFGenerator();

  if (!recipe) return null;

  const handleSectionToggle = (section) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  // Calculate per-serving nutrition - rely on API calculations
  const servings = recipe.servings || 1;

  // Use API-calculated nutrition values as the source of truth
  const totalNutrition = {
    calories: parseFloat(recipe.kCals) || 0,
    protein: parseFloat(recipe.proteins) || 0,
    carbs: parseFloat(recipe.carbs) || 0,
    fats: parseFloat(recipe.fats) || 0,
  };

  // Calculate per-serving nutrition from API totals
  const perServingNutrition = {
    calories: Math.round(totalNutrition.calories / servings),
    protein: Math.round(totalNutrition.protein / servings),
    carbs: Math.round(totalNutrition.carbs / servings),
    fats: Math.round(totalNutrition.fats / servings),
  };

  // Get timing info from API response
  const timingInfo = recipe.timingInfo || {
    totalTime: 0,
    prepTime: 0,
    cookTime: 0,
  };

  // Handle HTML to PDF conversion
  const handleDownloadHTMLPDF = async () => {
    try {
      const fileName = `${recipe.name ? recipe.name.replace(/[^a-z0-9]/gi, "_").toLowerCase() : "recipe"}_recipe.pdf`;
      await generatePDFFromHTML("recipe-content", fileName);
    } catch (error) {
      console.error("Error generating HTML PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  // Handle share functionality
  const handleShare = async () => {
    try {
      if (navigator.share && navigator.canShare) {
        await navigator.share({
          title: recipe.name || "Recipe",
          text: `Check out this delicious recipe: ${recipe.name || "Recipe"}${
            recipe.description ? " - " + recipe.description : ""
          }`,
          url: window.location.href,
        });
      } else {
        // Fallback: copy to clipboard
        const shareText = `${recipe.name || "Recipe"}

${recipe.description || ""}

${window.location.href}`;
        await navigator.clipboard.writeText(shareText);
        console.log("Recipe details copied to clipboard!");
      }
    } catch (error) {
      console.log("Error sharing recipe:", error);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }} id="recipe-content">
      {/* Header with action buttons */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button
          startIcon={<PdfIcon />}
          variant="outlined"
          sx={{ textTransform: "none" }}
          onClick={handleDownloadHTMLPDF}
          disabled={isGenerating}
        >
          {isGenerating ? "Generating PDF..." : "View PDF"}
        </Button>

        <Button
          startIcon={<ShareIcon />}
          variant="outlined"
          sx={{ textTransform: "none" }}
          onClick={handleShare}
        >
          Share Recipe
        </Button>
      </Stack>

      {/* Recipe Header Card with Gradient Background */}
      <Card
        sx={{
          mb: 4,
          background: `linear-gradient(135deg, ${getMealTypeColor(
            recipe.mealType,
            theme,
          )} 0%, ${getMealTypeColor(recipe.mealType, theme)}CC 100%)`,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 4, position: "relative", zIndex: 1 }}>
          <Stack spacing={2}>
            {/* Meal type chip */}
            <Box>
              <Chip
                label={
                  recipe.mealType?.charAt(0).toUpperCase() +
                  recipe.mealType?.slice(1)
                }
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: 600,
                }}
              />
            </Box>

            {/* Recipe name */}
            <Typography variant="h2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {recipe.name}
            </Typography>

            {/* Organization branding */}
            {organizationBranding?.organizationName && (
              <Typography
                variant="h6"
                sx={{ opacity: 0.9, fontWeight: 400, fontStyle: "italic" }}
              >
                Recipe by {organizationBranding.organizationName}
              </Typography>
            )}

            {/* Description */}
            {recipe.description && (
              <Box>
                {(() => {
                  const { firstSentence, remainingContent } =
                    parseDescriptionSentences(recipe.description);

                  if (firstSentence && remainingContent) {
                    // Description with multiple sentences - show first sentence, then expand to full
                    return (
                      <>
                        {isDescriptionExpanded ? (
                          // Expanded state: Show first sentence bold + remaining content normal weight
                          <>
                            <Typography
                              variant="h5"
                              sx={{
                                opacity: 0.98,
                                fontWeight: 600,
                                maxWidth: "800px",
                                mb: 1,
                                lineHeight: 1.3,
                                display: "inline",
                              }}
                            >
                              {firstSentence}{" "}
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                opacity: 0.9,
                                fontWeight: 400,
                                maxWidth: "800px",
                                lineHeight: 1.3,
                                display: "inline",
                              }}
                            >
                              {remainingContent}
                            </Typography>
                          </>
                        ) : (
                          // Collapsed state: Show only first sentence bold
                          <Typography
                            variant="h5"
                            sx={{
                              opacity: 0.98,
                              fontWeight: 600,
                              maxWidth: "800px",
                              mb: 1,
                              lineHeight: 1.3,
                            }}
                          >
                            {firstSentence}
                          </Typography>
                        )}

                        {/* Read more/less button */}
                        <Button
                          variant="text"
                          size="small"
                          onClick={() =>
                            setIsDescriptionExpanded(!isDescriptionExpanded)
                          }
                          sx={{
                            color: "rgba(255,255,255,0.8)",
                            textTransform: "none",
                            p: 0,
                            mt: 1,
                            minHeight: "auto",
                            display: "block",
                            "&:hover": {
                              color: "white",
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          {isDescriptionExpanded ? "Show less" : "Read more..."}
                        </Button>
                      </>
                    );
                  } else {
                    // Single sentence or short description - show as is
                    return (
                      <Typography
                        variant="h5"
                        sx={{
                          opacity: 0.98,
                          fontWeight: 600,
                          maxWidth: "800px",
                          lineHeight: 1.3,
                        }}
                      >
                        {recipe.description}
                      </Typography>
                    );
                  }
                })()}
              </Box>
            )}

            {/* Quick stats */}
            <Stack
              direction="row"
              spacing={4}
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 3 }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    width: 32,
                    height: 32,
                  }}
                >
                  <RestaurantIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.9, lineHeight: 1 }}
                  >
                    Servings
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, lineHeight: 1 }}
                  >
                    {servings}
                  </Typography>
                </Box>
              </Box>

              {timingInfo.totalTime > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      width: 32,
                      height: 32,
                    }}
                  >
                    <AccessTimeIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.9, lineHeight: 1 }}
                    >
                      Total Time
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, lineHeight: 1 }}
                    >
                      {timingInfo.totalTime} min
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    width: 32,
                    height: 32,
                  }}
                >
                  <CaloriesIcon fontSize="small" />
                </Avatar>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.9, lineHeight: 1 }}
                  >
                    Per Serving
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, lineHeight: 1 }}
                  >
                    {perServingNutrition.calories} kcal
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <Grid container spacing={4}>
        {/* Left Column - Recipe Details */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={3}>
            {/* Ingredients Section */}
            <Accordion
              expanded={expandedSections.has("ingredients")}
              onChange={() => handleSectionToggle("ingredients")}
              sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  🛒 Ingredients
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List sx={{ pt: 0 }}>
                  {recipe.ingredients?.map((ingredient, index) => (
                    <ListItem
                      key={ingredient.id || index}
                      sx={{ px: 0, py: 1 }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {ingredient.amount} {ingredient.unit}{" "}
                            {ingredient.name}
                          </Typography>
                        }
                        secondary={(() => {
                          const proteins = ingredient.proteins || 0;
                          const carbs = ingredient.carbs || 0;
                          const fats = ingredient.fats || 0;
                          const calories = ingredient.kCals || 0;

                          if (proteins || carbs || fats || calories) {
                            return (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {calories > 0 &&
                                  `${Math.round(calories)} kcal, `}
                                {Math.round(proteins)}g protein,{" "}
                                {Math.round(carbs)}g carbs, {Math.round(fats)}g
                                fats
                              </Typography>
                            );
                          }
                          return null;
                        })()}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>

            {/* Instructions Section */}
            <Accordion
              expanded={expandedSections.has("instructions")}
              onChange={() => handleSectionToggle("instructions")}
              sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  👨‍🍳 Instructions
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={3}>
                  {recipe.instructions?.map((instruction, index) => (
                    <Box key={instruction.id || index}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="flex-start"
                      >
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 32,
                            height: 32,
                            fontSize: "0.875rem",
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                            {instruction.instruction || instruction.step || ""}
                          </Typography>
                          {instruction.duration && (
                            <Chip
                              label={formatDuration(instruction.duration)}
                              size="small"
                              icon={<AccessTimeIcon />}
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                      </Stack>
                      {index < recipe.instructions.length - 1 && (
                        <Divider sx={{ my: 2, ml: 5 }} />
                      )}
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Grid>

        {/* Right Column - Nutrition & Info */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Nutrition Facts */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  📊 Nutrition Facts
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Per serving (1 of {servings})
                </Typography>

                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      Calories
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {perServingNutrition.calories}
                    </Typography>
                  </Box>
                  <Divider />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">Protein</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {perServingNutrition.protein}g
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">Carbohydrates</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {perServingNutrition.carbs}g
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2">Fats</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {perServingNutrition.fats}g
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Timing breakdown */}
            {(timingInfo.prepTime > 0 || timingInfo.cookTime > 0) && (
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    ⏱️ Timing
                  </Typography>
                  <Stack spacing={2}>
                    {timingInfo.prepTime > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2">Prep Time</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {timingInfo.prepTime} min
                        </Typography>
                      </Box>
                    )}
                    {timingInfo.cookTime > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2">Cook Time</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {timingInfo.cookTime} min
                        </Typography>
                      </Box>
                    )}
                    {timingInfo.totalTime > 0 && (
                      <>
                        <Divider />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            Total Time
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {timingInfo.totalTime} min
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Cooking Tips */}
            <Card sx={{ bgcolor: "info.light" }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  💡 Pro Tips
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    • Read all instructions before starting
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    • Prep all ingredients first (mise en place)
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    • Taste and adjust seasoning as needed
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    • Clean as you go to stay organized
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ClientRecipeContent;
